import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import CloseIcon from "@mui/icons-material/Close";
import { axiosConfig } from "../../../axios/axiosConfig";
import toast from "react-hot-toast";
import RefundAccessList from "./RefundAccessList";

const RefundModuleAccess = ({ asTab = false }) => {
  const [open, setOpen] = useState(false);
  const [phoneno, setPhoneno] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleOpen = () => {
    setOpen(true);
    setPhoneno("");
    setError(null);
  };

  const handleClose = () => setOpen(false);

  const handleGrant = async () => {
    if (!phoneno.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await axiosConfig({
        method: "post",
        url: "payment/refund-module-access/",
        data: { phoneno: Number(phoneno) },
      });
      setOpen(false);
      setRefreshKey((k) => k + 1);
      toast.success("Refund access granted successfully!");
    } catch (err) {
      const msg = err?.response?.data?.message || "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const grantButton = (
    <Button
      variant="contained"
      onClick={handleOpen}
      sx={{
        background: "linear-gradient(135deg, #016551, #014d3d)",
        color: "#fff",
        fontWeight: 600,
        fontSize: "13px",
        textTransform: "none",
        borderRadius: "8px",
        px: 2.5,
        py: 1,
        boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
        "&:hover": {
          background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
          boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
        },
      }}
    >
      + Grant Access
    </Button>
  );

  const grantDialog = (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(4,52,58,0.18)",
        },
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #04343a 0%, #016551 100%)",
          px: 3,
          pt: 3,
          pb: 2.5,
          position: "relative",
        }}
      >
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "#fff", background: "rgba(255,255,255,0.12)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LockOpenIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "16px", lineHeight: 1.3 }}>
              Grant Refund Access
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
              Enter the user's contact number below
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3, background: "#f8fafc" }}>
        <Box
          sx={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            p: 2.5,
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
          }}
        >
          <Typography
            sx={{ fontSize: "11px", fontWeight: 600, color: "#016551", mb: 1, textTransform: "uppercase", letterSpacing: "0.5px" }}
          >
            Contact Number
          </Typography>
          <TextField
            placeholder="e.g. 9876543210"
            value={phoneno}
            onChange={(e) => setPhoneno(e.target.value)}
            fullWidth
            type="tel"
            size="small"
            inputProps={{ maxLength: 15 }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: "#016551", fontSize: "14px", fontWeight: 600, userSelect: "none" }}>
                  +91
                </Box>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#f8fafc",
                fontSize: "14px",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#016551" },
                "&.Mui-focused fieldset": { borderColor: "#016551", borderWidth: "1.5px" },
              },
            }}
          />
        </Box>
        {error && (
          <Box
            sx={{
              mt: 2,
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              borderRadius: "10px",
              p: 2,
            }}
          >
            <Typography sx={{ fontSize: "13px", color: "#be123c", fontWeight: 500 }}>
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, background: "#f8fafc", gap: 1.5, justifyContent: "stretch" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: "9px",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            py: 1.1,
            borderColor: "#cbd5e1",
            color: "#64748b",
            "&:hover": { borderColor: "#94a3b8", background: "#fff" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleGrant}
          variant="contained"
          fullWidth
          disabled={loading || !phoneno.trim()}
          sx={{
            borderRadius: "9px",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 700,
            py: 1.1,
            background: "linear-gradient(135deg, #016551, #014d3d)",
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
              boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
            },
            "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
          }}
        >
          {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Grant Access"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (asTab) {
    return (
      <>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          {grantButton}
        </Box>
        <RefundAccessList refreshKey={refreshKey} />
        {grantDialog}
      </>
    );
  }

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <LockOpenIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
            Refund Module Access
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
            Manage access permissions for the refund module
          </Typography>
        </Box>
        {grantButton}
      </Box>
      <RefundAccessList refreshKey={refreshKey} />
      {grantDialog}
    </Box>
  );
};

export default RefundModuleAccess;
